import {
  differenceInYears,
  parseISO,
  format,
  setYear,
  isBefore,
  isValid,
  parse,
} from 'date-fns';

/**
 * Calculates the current age based on a date of birth string (YYYY-MM-DD).
 * @param dobString - The date of birth string.
 * @returns The current age in years, or null if the date is invalid.
 */
export function calculateAge(dobString: string): number | null {
  if (!dobString) return null;
  const birthDate = parseISO(dobString);
  if (!isValid(birthDate)) return null;
  return differenceInYears(new Date(), birthDate);
}

/**
 * Calculates the next birthday date from a date of birth string.
 * @param dobString - The date of birth string (YYYY-MM-DD).
 * @returns A Date object representing the next birthday, or null if invalid.
 */
export function getNextBirthday(dobString: string): Date | null {
  if (!dobString) return null;
  const birthDate = parseISO(dobString);
  if (!isValid(birthDate)) return null;
  
  const today = new Date();
  
  // Set time to 00:00:00 to avoid issues with timezones and DST
  today.setHours(0, 0, 0, 0);
  birthDate.setHours(0, 0, 0, 0);

  const currentYear = today.getFullYear();
  let nextBirthday = setYear(birthDate, currentYear);

  if (isBefore(nextBirthday, today)) {
    nextBirthday = setYear(nextBirthday, currentYear + 1);
  }

  return nextBirthday;
}

/**
 * Gets the day of the week for a given date.
 * @param date - The date to get the day of the week for.
 * @returns The full name of the day (e.g., "Monday"), or null if invalid.
 */
export function getDayOfWeek(date: Date | string | null): string | null {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(dateObj)) return null;
  return format(dateObj, 'EEEE');
}

/**
 * Formats a date into a user-friendly string (e.g., "April 20, 1990").
 * @param date - The date to format.
 * @returns A formatted date string, or null if invalid.
 */
export function formatDate(date: Date | string | null): string | null {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  if (!isValid(dateObj)) return null;
  return format(dateObj, 'MMMM d, yyyy');
}

/**
 * Formats a time string (HH:mm) into a user-friendly format (e.g., "09:30 AM").
 * @param timeString - The time string to format.
 * @returns A formatted time string, or null if invalid.
 */
export function formatTime(timeString: string | null | undefined): string | null {
  if (!timeString) return null;
  try {
    const time = parse(timeString, 'HH:mm', new Date());
    if (!isValid(time)) return null;
    return format(time, 'p'); // 'p' is for localized time, e.g., "7:00 PM"
  } catch (e) {
    return null;
  }
}
